import React, { useState } from "react";

import {
  DefaultButton,
  IContextualMenuItem,
  mergeStyleSets,
  SplitButton,
  Stack,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { SectionTitle } from "@components/SectionTitle";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  FieldDevice,
  FieldDeviceCacheOption,
  FieldDeviceCacheType
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useClearDeviceCaches } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { ConfigDiffModal } from "./ConfigDiffModal";
import { DesiredConfigModal } from "./DesiredConfigModal";
import { DeviceSettings } from "./device-settings";
import { ReportedConfigModal } from "./ReportedConfigModal";

const CLEAR_CACHE_TOOLTIP =
  "Clears the field configuration distributed cache and request cache for this device.";

interface FieldDeviceConfigurationProps {
  device: FieldDevice;
}

export const FieldDeviceConfiguration: React.FC<FieldDeviceConfigurationProps> = ({
  device
}) => {
  const theme = useTheme();
  const [showDesiredConfig, setShowDesiredConfig] = useState<boolean>(false);
  const [showReportedConfig, setShowReportedConfig] = useState<boolean>(false);
  const [showConfigDiff, setShowConfigDiff] = useState<boolean>(false);

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const clearDeviceCache = useClearDeviceCaches(device.id);

  const cacheOptions: Array<FieldDeviceCacheOption> = [
    { type: FieldDeviceCacheType.Configuration, text: "Configuration" },
    { type: FieldDeviceCacheType.Claims, text: "Claims" },
    { type: FieldDeviceCacheType.CloudConnectorId, text: "Cloud Connector Id" },
    {
      type: FieldDeviceCacheType.AuthFieldClientProfile,
      text: "Auth Client Profile"
    },
    {
      type: FieldDeviceCacheType.FieldDevice,
      text: "Field Device"
    },
    { type: FieldDeviceCacheType.All, text: "All" }
  ];

  const clearCacheOptions: IContextualMenuItem[] = cacheOptions.map(option => ({
    key: option.type.toString(),
    text: option.text,
    onClick() {
      clearDeviceCache(option);
    }
  }));

  return (
    <>
      <SectionTitle>Device Configuration</SectionTitle>

      <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
        <Stack
          tokens={{ childrenGap: theme.spacing.m }}
          horizontalAlign="start"
        >
          <DefaultButton onClick={() => setShowDesiredConfig(true)}>
            Desired Field Configuration Preview
          </DefaultButton>
          <DefaultButton onClick={() => setShowReportedConfig(true)}>
            Reported Field Configuration Preview
          </DefaultButton>
          <DefaultButton onClick={() => setShowConfigDiff(true)}>
            Configuration Diff
          </DefaultButton>

          <TooltipHost content={CLEAR_CACHE_TOOLTIP}>
            <SplitButton
              styles={mergeStyleSets(dangerButtonStyles(theme))}
              splitButtonMenuProps={{
                primary: true
              }}
              disabled={!hasWritePermission}
              title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
              items={clearCacheOptions}
              onClick={() =>
                clearDeviceCache({
                  type: FieldDeviceCacheType.All,
                  text: "All"
                })
              }
            >
              Clear Field Device Cache
            </SplitButton>
          </TooltipHost>
        </Stack>

        <DeviceSettings device={device} />
      </Stack>
      <DesiredConfigModal
        show={showDesiredConfig}
        device={device}
        onDismiss={() => setShowDesiredConfig(false)}
      />
      <ReportedConfigModal
        show={showReportedConfig}
        device={device}
        onDismiss={() => setShowReportedConfig(false)}
      />
      <ConfigDiffModal
        show={showConfigDiff}
        device={device}
        onDismiss={() => setShowConfigDiff(false)}
      />
    </>
  );
};
